<script setup lang="ts">
import { type PropType } from 'vue';
import type { OrganisationOption } from './organisationOption';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaListItem from '@/components/design/composite/GaiaListItem.vue';

/**
 * The organisations a user belongs to, one row each, with the active one
 * marked and the two things you can do about a membership: switch into it or
 * leave it. Stateless — the profile page owns the switch and the leave dialog.
 */
const props = defineProps({
  organisations: {
    type: Array as PropType<OrganisationOption[]>,
    required: true,
  },
  activeUid: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** The organisation an action is in flight for; every row locks meanwhile. */
  busyUid: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ switch: [uid: string]; leave: [uid: string] }>();

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase();
}
</script>

<template>
  <n-flex v-if="props.organisations.length" vertical :size="8">
    <gaia-list-item
      v-for="organisation in props.organisations"
      :key="organisation.uid"
      variant="card"
      size="item"
      :clickable="false"
    >
      <template #leading>
        <gaia-avatar variant="mark">{{ initialOf(organisation.name) }}</gaia-avatar>
      </template>
      <n-flex align="center" :size="8" :wrap="false">
        <gaia-text weight="semibold" truncate>{{ organisation.name }}</gaia-text>
        <gaia-status-chip v-if="organisation.uid === props.activeUid" tone="success" size="small">
          Active
        </gaia-status-chip>
      </n-flex>
      <template v-if="organisation.description" #footer>
        <gaia-text tone="subtle" truncate>{{ organisation.description }}</gaia-text>
      </template>
      <template #trailing>
        <n-flex :size="6" :wrap="false">
          <gaia-button
            v-if="organisation.uid !== props.activeUid"
            size="small"
            :loading="props.busyUid === organisation.uid"
            :disabled="!!props.busyUid && props.busyUid !== organisation.uid"
            @click="emit('switch', organisation.uid)"
          >
            Switch
          </gaia-button>
          <gaia-button
            size="small"
            type="error"
            tertiary
            :disabled="!!props.busyUid"
            @click="emit('leave', organisation.uid)"
          >
            Leave
          </gaia-button>
        </n-flex>
      </template>
    </gaia-list-item>
  </n-flex>

  <gaia-empty-state v-else dashed title="No organisations">
    You are not a member of any organisation.
    <template v-if="$slots.actions" #actions>
      <slot name="actions" />
    </template>
  </gaia-empty-state>
</template>
